import {observer} from 'mobx-react-lite';
import './customer-edit.scss';
import {Button, Collapse, CollapseProps, message} from "antd";
import React, {useEffect, useState} from "react";
import {ReactComponent as CollapseIcon} from '../../../../assets/svg/chevron-down-circle.svg';
import {CustomerDetailsComponent} from "./customer-details/customer-details";
import {ControlCenterAccountsComponent} from "./cc-accounts/cc-accounts";
import {ApnsComponent} from "./apns/apns";
import {useStores} from "../../../store";
import {OemDetails} from "../../../../dtos/oem-details.dto";
import {useNavigate} from "react-router";
import {useParams} from "react-router-dom";
import LoaderComponent from "../../../shared/loader/loader-component";
import {CreateAdxDbModal} from "./create-adx-db/create-adx-db-modal";
import CustomModal from "../../../shared/custom-modal/custom-modal";
import {OnboardingStatus} from "../../../../utils/enums";
import {OemConfiguration} from "../../../../dtos/oem-configuration.dto";

export const CustomerEditComponent = observer(() => {
    const navigate = useNavigate();
    const {customerStore, uxStore} = useStores();
    const [oemDetails, setOemDetails] = useState({} as OemDetails);
    const [oemAccountDetailsList, setOemAccountDetailsList] = useState([]);
    const [apns, setApns] = useState([]);
    const [enableSave, setEnableSave] = useState(false);
    const [enableDraft, setEnableDraft] = useState(false);
    const {oemName} = useParams<{ oemName?: string }>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isCustomerDetailsValid, setIsCustomerDetailsValid] = useState(oemName != null);
    const oemConfiguration = {oemDetails, oemAccountDetailsList, apns} as OemConfiguration;

    const items: CollapseProps['items'] = [
        {
            key: '1',
            label: 'Customer details',
            children: <CustomerDetailsComponent onDetailsChange={setOemDetails} oemName={oemName}
                                                onValidityChange={setIsCustomerDetailsValid}/>,
        },
        {
            key: '2',
            label: 'Control Center accounts',
            children: <ControlCenterAccountsComponent onDetailsChange={setOemAccountDetailsList} oemName={oemName}/>,
        },
        {
            key: '3',
            label: 'APNs',
            children: <ApnsComponent onDetailsChange={setApns} oemName={oemName}/>,
        },
    ];

    useEffect(() => {
        if (customerStore.oemOnboardingDetails.length == 0) {
            customerStore.getOemOnboardingDetailsList().then();
        }
        if (oemName) {
            const oemDetails = customerStore.oemOnboardingDetails.find(oem => oem.name == oemName);
            setIsUpdate((oemDetails && oemDetails.status != OnboardingStatus.DRAFT));
        }
    }, [customerStore, oemName]);

    useEffect(() => {
        updateValidity(oemConfiguration);
    }, [isCustomerDetailsValid, oemDetails, oemAccountDetailsList, apns]);

    function updateValidity(updatedOemConfiguration: OemConfiguration) {
        const {oemDetails, oemAccountDetailsList, apns} = updatedOemConfiguration;
        const isComplete =
            oemDetails.name && oemDetails.label && oemDetails.sfName && oemDetails.locationType?.length > 0 &&
            oemDetails.reportDl?.length > 0 && oemDetails.alertsDl?.length > 0 && oemDetails.ratTypeList?.length > 0 &&
            oemAccountDetailsList.length > 0 && oemAccountDetailsList.every((item: any) => item.id && item.name) &&
            apns.length > 0;
        setEnableSave(isCustomerDetailsValid && isComplete);
        setEnableDraft(isCustomerDetailsValid && oemDetails?.name && oemAccountDetailsList.every((item: any) => item.id && item.name));
    }

    function onSaveDraftClick() {
        customerStore.setIsLoading(true);
        customerStore.saveOemConfiguration({oemConfiguration}).then(() => {
                message.success('Draft saved successfully!').then();
                customerStore.getOemOnboardingDetailsList().then();
                navigate('../customers');
            }
        ).catch(() => {
            message.error('Draft save process failed').then();
        }).finally(() => {
            customerStore.setIsLoading(false);
        });
    }

    function onStartOnboardingClick() {
        customerStore.setIsLoading(true);
        customerStore.saveOemConfiguration({oemConfiguration}).then(() => {
                message.success('Draft saved successfully!').then();
                customerStore.getOemOnboardingDetailsList().then();
                navigate(`../customer-edit/${oemDetails.name}`, {replace: false});
                startOemOnboarding();
            }
        ).catch(() => {
            customerStore.setIsLoading(false);
            setIsErrorModalOpen(true);
        });
    }

    function startOemOnboarding() {
        customerStore.setIsLoading(true);
        customerStore.startOemOnboarding(oemDetails.name).then((res: any) => {
            if (res?.dbFound) {
                message.success('Oem onboarded successfully!').then();
                navigate('../customers');
            } else {
                setIsModalOpen(true);
            }
        }).catch(() => {
            setIsErrorModalOpen(true);
        }).finally(() => {
            customerStore.setIsLoading(false);
        });
    }

    function onCreateAdxDbModalClose(tryAgain: boolean) {
        setIsModalOpen(false);
        if (tryAgain) {
            startOemOnboarding();
        }
    }

    return <>
        <div className='details-container'>
            <div className='details-header'>
                <div className='header-title'>Add customer</div>
                <div className='header-buttons'>
                    <Button className={'secondary-btn'} type="primary"
                            onClick={() => navigate('../customers')}>Cancel</Button>
                    {isUpdate ?
                        <Button type="primary" disabled={!enableSave}
                                onClick={onSaveDraftClick}>Update customer</Button>
                        : <>
                            <Button className='secondary-btn' type="primary" disabled={!enableDraft}
                                    onClick={onSaveDraftClick}>Save Draft</Button>
                            <Button type="primary" disabled={!enableSave}
                                    onClick={onStartOnboardingClick}>Start Onboarding</Button>
                        </>
                    }
                </div>
            </div>
            <div>
                <Collapse items={items} defaultActiveKey={['1', '2', '3']} bordered={false}
                          expandIcon={({isActive}) => (
                              <CollapseIcon style={{
                                  transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                              }}/>
                          )}/>;
            </div>
        </div>
        {uxStore.showLoader && <LoaderComponent/>}
        {isModalOpen &&
            <CreateAdxDbModal oem={oemDetails.name}
                              closeModal={onCreateAdxDbModalClose}/>}
        {isErrorModalOpen &&
            <CustomModal
                isOpen={true} width={750}
                title={`Could not verify ADX database for customer ${oemDetails.name}`}
                onOk={() => setIsErrorModalOpen(false)}
                onCancel={() => setIsErrorModalOpen(false)}
                cancelButtonProps={{className: 'hide'}}
                okText="OK">
                <div>Please check the MTAD server and try again later</div>
                <br/><br/><br/>
            </CustomModal>}
    </>;
});